import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Mail, Lock, Loader2, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Match the Web Marketing Machine host login (IBM Plex Sans, stone palette).
const PLEX = '"IBM Plex Sans", ui-sans-serif, system-ui, -apple-system, sans-serif';
const ARIAL_BLACK = '"Arial Black", "Arial Bold", Arial, sans-serif';

/** Co-branded logo stack: WMM emblem (spinning arrow ring) + Interact
 * Marketing wordmark + the app's name in Arial Black. Assets are served
 * locally from /branding so the page never depends on the WMM host. */
export const WmmBrandStack = ({ size = 110 }) => (
  <div className="flex flex-col items-center select-none">
    <div className="relative" style={{ width: size, height: size }}>
      <img src="/branding/wmm-logo-core.png" alt="Web Marketing Machine" className="absolute inset-0 h-full w-full" />
      <img src="/branding/wmm-logo-ring.png" alt="" aria-hidden="true" className="wmm-ring-spin absolute inset-0 h-full w-full" />
    </div>
    <img src="/branding/interact-marketing.png" alt="Interact Marketing" className="mt-2 w-[150px]" />
    <p
      data-testid="login-app-title"
      className="mt-3 text-[15px] uppercase text-[#1c1917]"
      style={{ fontFamily: ARIAL_BLACK, fontWeight: 900, letterSpacing: "0.04em" }}
    >
      WordPress Site Builder
    </p>
  </div>
);

const FieldLabel = ({ htmlFor, children }) => (
  <label
    htmlFor={htmlFor}
    className="block text-[12px] font-medium uppercase text-[#57534e]"
    style={{ letterSpacing: "0.6px" }}
  >
    {children}
  </label>
);

/**
 * Cold-hit login screen, rebranded to mirror the WMM host's login page.
 * Primary path: bounce to WMM (it owns all passwords) which returns the user
 * to /sso with a one-shot token. Secondary: emergency operator credentials.
 */
export default function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, loading, loginLocal } = useAuth();

  const [wmmBaseUrl, setWmmBaseUrl] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [showLocal, setShowLocal] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState(null);

  const ssoFailed = params.get("error") === "sso_failed";

  // Already signed in? Straight into the app.
  useEffect(() => {
    if (!loading && user) navigate("/", { replace: true });
  }, [loading, user, navigate]);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/auth/config`)
      .then(({ data }) => {
        if (alive) setWmmBaseUrl(data.wmm_base_url || null);
      })
      .catch(() => {
        if (alive) setWmmBaseUrl(null);
      })
      .finally(() => {
        if (alive) setConfigLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const goToWmm = () => {
    if (!wmmBaseUrl) return;
    const returnTo = encodeURIComponent(`${window.location.origin}/sso`);
    window.location.href = `${wmmBaseUrl}/login?returnTo=${returnTo}`;
  };

  const submitLocal = async (e) => {
    e.preventDefault();
    setLocalError(null);
    setBusy(true);
    try {
      await loginLocal(email, password);
      navigate("/", { replace: true });
    } catch (err) {
      setLocalError(err?.response?.data?.detail || "Invalid credentials.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="login-page"
      className="min-h-screen bg-[#fafafa] flex flex-col items-center justify-center px-4 py-10"
      style={{ fontFamily: PLEX }}
    >
      <WmmBrandStack />

      <div className="mt-7 w-full max-w-[420px] rounded-[14px] border border-black/[0.06] bg-white px-6 py-6 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_16px_40px_rgba(0,0,0,0.06)]">
        {ssoFailed && (
          <div
            data-testid="login-error-banner"
            className="mb-4 flex items-start gap-2 rounded-[6px] border border-[#fecaca] bg-[#fef2f2] px-3 py-2.5"
          >
            <AlertTriangle size={14} className="mt-0.5 shrink-0 text-[#b91c1c]" />
            <p className="text-[12px] leading-snug text-[#b91c1c]">
              Single sign-on failed - the handoff token was missing, expired, or rejected. Try again from the Web
              Marketing Machine, or use operator credentials below.
            </p>
          </div>
        )}

        <button
          type="button"
          data-testid="login-wmm-button"
          onClick={goToWmm}
          disabled={configLoading || !wmmBaseUrl}
          className="flex h-10 w-full items-center justify-center rounded-[6px] bg-[#1c1917] text-[14px] font-medium text-white transition-colors duration-150 hover:bg-[#292524] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1c1917] disabled:cursor-not-allowed disabled:bg-[#a8a29e]"
        >
          {configLoading ? <Loader2 size={16} className="animate-spin" /> : "Sign in with Web Marketing Machine"}
        </button>
        {!configLoading && !wmmBaseUrl && (
          <p data-testid="login-wmm-unavailable" className="mt-2 text-center text-[11px] text-[#a8a29e]">
            WMM host is not configured - use operator credentials below.
          </p>
        )}

        <div className="my-5 flex items-center gap-3">
          <div className="h-px flex-1 bg-black/[0.07]" />
          <span className="text-[11px] font-medium uppercase text-[#a8a29e]" style={{ letterSpacing: "0.6px" }}>
            or
          </span>
          <div className="h-px flex-1 bg-black/[0.07]" />
        </div>

        {!showLocal ? (
          <button
            type="button"
            data-testid="login-local-toggle"
            onClick={() => setShowLocal(true)}
            className="flex h-10 w-full items-center justify-center rounded-[6px] border border-[#e7e5e4] text-[13px] font-medium text-[#57534e] transition-colors duration-150 hover:border-[#d6d3d1] hover:bg-[#fafaf9]"
          >
            Use operator credentials
          </button>
        ) : (
          <form onSubmit={submitLocal} className="space-y-4" data-testid="login-local-form">
            <div className="space-y-1.5">
              <FieldLabel htmlFor="login-email">Email</FieldLabel>
              <div className="relative">
                <Mail size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#a8a29e]" />
                <input
                  id="login-email"
                  data-testid="login-local-email"
                  type="email"
                  autoComplete="username"
                  placeholder="you@interactmarketing.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-10 w-full rounded-[6px] border border-[#e7e5e4] bg-white pl-9 pr-3 text-[14px] text-[#1c1917] placeholder:text-[#a8a29e] focus:border-[#a8a29e] focus:outline-none focus:ring-2 focus:ring-black/[0.06]"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <FieldLabel htmlFor="login-password">Password</FieldLabel>
              <div className="relative">
                <Lock size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#a8a29e]" />
                <input
                  id="login-password"
                  data-testid="login-local-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-10 w-full rounded-[6px] border border-[#e7e5e4] bg-white pl-9 pr-3 text-[14px] text-[#1c1917] focus:border-[#a8a29e] focus:outline-none focus:ring-2 focus:ring-black/[0.06]"
                />
              </div>
            </div>
            {localError && (
              <p data-testid="login-local-error" className="text-[12px] text-[#b91c1c]">
                {localError}
              </p>
            )}
            <button
              type="submit"
              data-testid="login-local-submit"
              disabled={busy}
              className="flex h-10 w-full items-center justify-center rounded-[6px] bg-[#1c1917] text-[14px] font-medium text-white transition-colors duration-150 hover:bg-[#292524] disabled:bg-[#a8a29e]"
            >
              {busy ? <Loader2 size={15} className="animate-spin" /> : "Sign in"}
            </button>
          </form>
        )}
      </div>

      <p className="mt-5 max-w-[420px] text-center text-[12px] leading-relaxed text-[#78716c]">
        Access is provisioned by an administrator. If you need an account, contact your IMM representative.
      </p>
    </div>
  );
}
